import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot, 
  query, 
  where,
  orderBy, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  signInAnonymously,
  updateProfile,
  User
} from 'firebase/auth';

import firebaseConfig from '../../firebase-applet-config.json';
import { 
  MediaItem, 
  EpisodeInfo, 
  CuratedCollection, 
  UserProfile, 
  WatchlistItem, 
  WatchHistoryItem, 
  AnimeCatJsonRoot, 
  SeriesListItem, 
  MovieInfo, 
  SyncSource, 
  SyncResult,
  IptvSource,
  IptvFavorite
} from '../types';
import { INITIAL_MEDIA_ITEMS, INITIAL_COLLECTIONS, ANIME_AVATARS } from '../data/initialCatalog';
import { fetchRemoteJson, normalizeRemoteUrl } from '../utils/fetchJson';
import { cleanGenres } from '../utils/genreUtils';

// Initialize Firebase App
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

// Initialize Firestore targeting the specific database 'animecat'
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId || 'animecat');
export const auth = getAuth(app);

// Helper to sanitize username into a clean email format for Firebase Auth
export function usernameToEmail(username: string): string {
  const clean = username.trim().toLowerCase().replace(/[^a-z0-9._-]/g, '');
  return `${clean || 'user'}@animecat.cat`;
}

// ----------------------------------------------------
// AUTHENTICATION SERVICES
// ----------------------------------------------------

// Admin email detection
const ADMIN_EMAILS = ['josep.g.frigole2@gmail.com', 'admin@animecat.cat'];

/**
 * Ensures that the system 'admin' user document exists in Firestore (garcollections2)
 * with role: 'admin', status: 'active', and password: 'admin' (which can be edited directly in Firestore).
 */
export async function ensureAdminUserCreated(): Promise<UserProfile> {
  try {
    const adminDocRef = doc(db, 'users', 'admin');
    const snap = await getDoc(adminDocRef);

    if (snap.exists()) {
      const data = snap.data() as UserProfile;
      // Ensure admin has role 'admin' and status 'active'
      if (data.role !== 'admin' || data.status !== 'active') {
        await updateDoc(adminDocRef, {
          role: 'admin',
          status: 'active'
        });
      }
      return { ...data, uid: 'admin', role: 'admin', status: 'active' };
    }

    // Create user admin document in Firestore
    const newAdmin: UserProfile = {
      uid: 'admin',
      username: 'admin',
      displayName: 'Administrador GarCollections',
      email: 'admin@animecat.cat',
      avatar: 'https://images.unsplash.com/photo-1534447677768-be436bb09401?w=150&auto=format&fit=crop&q=80',
      role: 'admin',
      status: 'active',
      password: 'admin', // Stored in Firestore so user can inspect and modify it directly
      createdAt: new Date().toISOString(),
      validatedAt: new Date().toISOString(),
      validatedBy: 'System',
      preferences: {
        autoPlayNext: true,
        skipIntroAuto: false,
        quality: '1080p'
      }
    };

    await setDoc(adminDocRef, sanitizeForFirestore(newAdmin));
    return newAdmin;
  } catch (err) {
    console.warn('ensureAdminUserCreated notice:', err);
    return {
      uid: 'admin',
      username: 'admin',
      displayName: 'Administrador GarCollections',
      email: 'admin@animecat.cat',
      avatar: 'https://images.unsplash.com/photo-1534447677768-be436bb09401?w=150&auto=format&fit=crop&q=80',
      role: 'admin',
      status: 'active',
      password: 'admin',
      createdAt: new Date().toISOString()
    };
  }
}

export async function registerWithUsername(username: string, password: string, displayName?: string, avatarUrl?: string): Promise<UserProfile> {
  const cleanUsername = username.trim().toLowerCase().replace(/[^a-z0-9._-]/g, '');
  const email = usernameToEmail(cleanUsername);
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  const defaultName = displayName?.trim() || username.trim();
  const defaultAvatar = avatarUrl || ANIME_AVATARS[0].url;
  const isAdmin = ADMIN_EMAILS.includes(email.toLowerCase()) || cleanUsername === 'admin';

  await updateProfile(user, {
    displayName: defaultName,
    photoURL: defaultAvatar
  });

  const profile: UserProfile = {
    uid: user.uid,
    username: cleanUsername,
    displayName: defaultName,
    email: email,
    avatar: defaultAvatar,
    role: isAdmin ? 'admin' : 'user',
    status: isAdmin ? 'active' : 'pending', // Users require admin validation to be active
    password: password, // Store in Firestore for easy viewing and management
    createdAt: new Date().toISOString(),
    validatedAt: isAdmin ? new Date().toISOString() : undefined,
    validatedBy: isAdmin ? 'System' : undefined,
    preferences: {
      autoPlayNext: true,
      skipIntroAuto: false,
      quality: '1080p'
    }
  };

  await setDoc(doc(db, 'users', user.uid), sanitizeForFirestore(profile));
  return profile;
}

export async function loginWithUsername(username: string, password: string): Promise<UserProfile> {
  const cleanUsername = username.trim().toLowerCase().replace(/[^a-z0-9._-]/g, '');
  const email = username.includes('@') ? username.trim() : usernameToEmail(cleanUsername);
  const isAdmin = ADMIN_EMAILS.includes(email.toLowerCase()) || cleanUsername === 'admin';
  
  // 1. Check if user document exists in Firestore (e.g. user 'admin' or custom credentials in Firestore)
  let firestoreUser: UserProfile | null = null;
  try {
    // Check doc 'users/admin' directly or by doc id
    const directDocSnap = await getDoc(doc(db, 'users', cleanUsername));
    if (directDocSnap.exists()) {
      firestoreUser = { ...directDocSnap.data(), uid: directDocSnap.id } as UserProfile;
    } else {
      // Query by username field
      const userQ = query(collection(db, 'users'), where('username', '==', cleanUsername));
      const qSnap = await getDocs(userQ);
      if (!qSnap.empty) {
        firestoreUser = { ...qSnap.docs[0].data(), uid: qSnap.docs[0].id } as UserProfile;
      }
    }
  } catch (err) {
    console.warn("Firestore user check notice:", err);
  }

  // If user document has a password field in Firestore (e.g. user modified password in Firestore db):
  if (firestoreUser && firestoreUser.password !== undefined && firestoreUser.password !== '') {
    if (firestoreUser.password !== password) {
      const err: any = new Error("Nom d'usuari o contrasenya incorrectes.");
      err.code = 'auth/wrong-password';
      throw err;
    }

    // Password matches what is stored in Firestore!
    // Establish Firebase Auth session so that request.auth != null for Firestore rules
    try {
      await signInWithEmailAndPassword(auth, firestoreUser.email || email, password);
    } catch (authErr) {
      // If Firebase Auth password was not updated or user does not exist in Auth, use anonymous auth
      if (!auth.currentUser) {
        await signInAnonymously(auth).catch(() => {});
      }
    }

    const updatedProfile: UserProfile = {
      ...firestoreUser,
      role: (isAdmin || firestoreUser.role === 'admin') ? 'admin' : (firestoreUser.role || 'user'),
      status: 'active',
      lastLoginAt: new Date().toISOString()
    };

    await updateDoc(doc(db, 'users', firestoreUser.uid), {
      lastLoginAt: updatedProfile.lastLoginAt,
      role: updatedProfile.role,
      status: 'active'
    }).catch(() => {});

    return updatedProfile;
  }

  // 2. Standard Firebase Auth flow
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;
  
  const userDocRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userDocRef);

  if (userSnap.exists()) {
    const data = userSnap.data() as UserProfile;
    // Upgrade to admin if matching admin credentials
    if (isAdmin && (data.role !== 'admin' || data.status !== 'active')) {
      data.role = 'admin';
      data.status = 'active';
      await updateDoc(userDocRef, { role: 'admin', status: 'active', lastLoginAt: new Date().toISOString() });
    } else {
      await updateDoc(userDocRef, { lastLoginAt: new Date().toISOString() }).catch(() => {});
    }
    return { ...data, uid: user.uid, role: isAdmin ? 'admin' : data.role };
  }

  // Create fallback profile if not exists
  const fallbackProfile: UserProfile = {
    uid: user.uid,
    username: cleanUsername || username.replace('@animecat.cat', ''),
    displayName: user.displayName || username,
    email: user.email || email,
    avatar: user.photoURL || ANIME_AVATARS[0].url,
    role: isAdmin ? 'admin' : 'user',
    status: isAdmin ? 'active' : 'pending',
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString()
  };

  await setDoc(userDocRef, sanitizeForFirestore(fallbackProfile));
  return fallbackProfile;
}

export async function loginAsGuest(): Promise<UserProfile> {
  const userCredential = await signInAnonymously(auth);
  const user = userCredential.user;

  const guestProfile: UserProfile = {
    uid: user.uid,
    username: `convidat_${user.uid.slice(0, 5)}`,
    displayName: 'Usuari Convidat',
    email: 'convidat@animecat.cat',
    avatar: ANIME_AVATARS[0].url,
    role: 'user',
    status: 'active', // Guests can explore the catalog
    createdAt: new Date().toISOString(),
    lastLoginAt: new Date().toISOString()
  };

  await setDoc(doc(db, 'users', user.uid), sanitizeForFirestore(guestProfile), { merge: true });
  return guestProfile;
}

export async function logoutUser(): Promise<void> {
  await signOut(auth);
}

export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      return snap.data() as UserProfile;
    }
  } catch (err) {
    console.warn('Error fetching user profile:', err);
  }
  return null;
}

export async function updateUserAvatar(uid: string, avatarUrl: string, displayName?: string): Promise<void> {
  const updates: Partial<UserProfile> = { avatar: avatarUrl };
  if (displayName) updates.displayName = displayName;
  
  await updateDoc(doc(db, 'users', uid), sanitizeForFirestore(updates));
  if (auth.currentUser) {
    await updateProfile(auth.currentUser, {
      photoURL: avatarUrl,
      ...(displayName ? { displayName } : {})
    });
  }
}

export async function updateUserPreferences(
  uid: string,
  preferences: {
    autoPlayNext?: boolean;
    skipIntroAuto?: boolean;
    quality?: '1080p' | '720p' | 'auto';
    subdlApiKey?: string;
  }
): Promise<void> {
  const updates = { preferences };
  await updateDoc(doc(db, 'users', uid), sanitizeForFirestore(updates));
}

// ----------------------------------------------------
// USER MANAGEMENT SERVICES (Admin panel operations)
// ----------------------------------------------------

export function subscribeToUsers(callback: (users: UserProfile[]) => void) {
  const q = collection(db, 'users');
  return onSnapshot(q, (snapshot) => {
    const users: UserProfile[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as UserProfile;
      users.push({
        ...data,
        uid: docSnap.id,
        // Fallback default status to 'active' for older accounts without status, or 'pending'
        status: data.status || (data.role === 'admin' ? 'active' : 'active')
      });
    });
    // Sort by createdAt descending
    users.sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return timeB - timeA;
    });
    callback(users);
  }, (err) => {
    console.warn('Firestore users listener error:', err);
    callback([]);
  });
}

export async function adminCreateUser(userData: {
  username: string;
  displayName?: string;
  email?: string;
  role?: 'user' | 'admin';
  status?: 'active' | 'pending' | 'blocked';
  avatar?: string;
  password?: string;
}): Promise<UserProfile> {
  const cleanUsername = userData.username.trim().toLowerCase().replace(/[^a-z0-9._-]/g, '');
  const email = userData.email?.trim() || usernameToEmail(cleanUsername);
  const uid = cleanUsername === 'admin' ? 'admin' : `user_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  
  const newProfile: UserProfile = {
    uid: uid,
    username: cleanUsername || userData.username.trim(),
    displayName: userData.displayName?.trim() || userData.username.trim(),
    email: email,
    avatar: userData.avatar || ANIME_AVATARS[0].url,
    role: userData.role || (cleanUsername === 'admin' ? 'admin' : 'user'),
    status: userData.status || 'active', // Default to active when created by admin
    password: userData.password || (cleanUsername === 'admin' ? 'admin' : '123456'),
    createdAt: new Date().toISOString(),
    validatedAt: userData.status === 'active' ? new Date().toISOString() : undefined,
    validatedBy: 'Admin',
    preferences: {
      autoPlayNext: true,
      skipIntroAuto: false,
      quality: '1080p'
    }
  };

  await setDoc(doc(db, 'users', uid), sanitizeForFirestore(newProfile));
  return newProfile;
}

export async function adminUpdateUser(uid: string, updates: Partial<UserProfile>): Promise<void> {
  const sanitized = sanitizeForFirestore(updates);
  await updateDoc(doc(db, 'users', uid), sanitized);
}

export async function adminValidateUser(uid: string, adminIdentifier?: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid), sanitizeForFirestore({
    status: 'active',
    validatedAt: new Date().toISOString(),
    validatedBy: adminIdentifier || 'Admin'
  }));
}

export async function adminBlockUser(uid: string): Promise<void> {
  await updateDoc(doc(db, 'users', uid), sanitizeForFirestore({
    status: 'blocked'
  }));
}

export async function adminDeleteUser(uid: string): Promise<void> {
  await deleteDoc(doc(db, 'users', uid));
}

export async function adminValidateAllPending(adminIdentifier?: string): Promise<number> {
  const snap = await getDocs(collection(db, 'users'));
  const batch = writeBatch(db);
  let count = 0;

  snap.forEach((docSnap) => {
    const user = docSnap.data() as UserProfile;
    if (user.status === 'pending') {
      batch.update(docSnap.ref, {
        status: 'active',
        validatedAt: new Date().toISOString(),
        validatedBy: adminIdentifier || 'Admin'
      });
      count++;
    }
  });

  if (count > 0) {
    await batch.commit();
  }
  return count;
}

// ----------------------------------------------------
// LOCAL CACHING & FAST BOOTSTRAP HELPERS
// ----------------------------------------------------

export function getCachedCatalog(): MediaItem[] {
  try {
    const cached = localStorage.getItem('animecat_cached_catalog');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Could not read cached catalog:', e);
  }
  return INITIAL_MEDIA_ITEMS;
}

export function getCachedCollections(): CuratedCollection[] {
  try {
    const cached = localStorage.getItem('animecat_cached_collections');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn('Could not read cached collections:', e);
  }
  return INITIAL_COLLECTIONS;
}

export function cacheCatalog(items: MediaItem[]): void {
  try {
    if (items && items.length > 0) {
      localStorage.setItem('animecat_cached_catalog', JSON.stringify(items));
      localStorage.setItem('animecat_last_cache_time', new Date().toISOString());
    }
  } catch (e) {
    // Ignore quota or private mode errors
  }
}

export function cacheCollections(cols: CuratedCollection[]): void {
  try {
    if (cols && cols.length > 0) {
      localStorage.setItem('animecat_cached_collections', JSON.stringify(cols));
    }
  } catch (e) {
    // Ignore quota or private mode errors
  }
}

// ----------------------------------------------------
// USER-SCOPED COLLECTIONS & CATALOG SERVICES
// ----------------------------------------------------

export async function autoSeedStarterCollectionForUser(userId: string): Promise<void> {
  if (!userId) return;
  try {
    const colsSnap = await getDocs(collection(db, 'users', userId, 'collections'));
    if (colsSnap.empty) {
      console.log(`Seeding initial collection for user ${userId}...`);
      const defaultColId = 'col-classics-catala';
      const colData: CuratedCollection = {
        id: defaultColId,
        userId: userId,
        title: 'AnimeCat Clàssics en Català',
        slug: 'classics-catala',
        description: "Col·lecció inicial amb clàssics de l'anime doblats al català (Bola de Drac, Slam Dunk, Sakura, Conan...).",
        seriesCount: INITIAL_MEDIA_ITEMS.filter(i => i.type === 'series').length,
        moviesCount: INITIAL_MEDIA_ITEMS.filter(i => i.type === 'movie').length,
        totalItems: INITIAL_MEDIA_ITEMS.length,
        sourceType: 'default',
        order: 1,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'users', userId, 'collections', defaultColId), sanitizeForFirestore(colData));

      // Batch write starter items to user catalog
      const batch = writeBatch(db);
      INITIAL_MEDIA_ITEMS.forEach(item => {
        const itemDoc = doc(db, 'users', userId, 'catalog', item.id);
        batch.set(itemDoc, sanitizeForFirestore({
          ...item,
          collectionId: defaultColId,
          collectionName: 'AnimeCat Clàssics en Català',
          userId: userId,
          updatedAt: new Date().toISOString()
        }));
      });
      await batch.commit();

      // Also create default sync source if needed
      const sourceDoc = doc(db, 'users', userId, 'sync_sources', DEFAULT_SYNC_SOURCES[0].id);
      await setDoc(sourceDoc, sanitizeForFirestore({
        ...DEFAULT_SYNC_SOURCES[0],
        userId: userId
      }));
    }
  } catch (err) {
    console.warn('Could not auto-seed user collection:', err);
  }
}

export function subscribeToUserCollections(userId: string | undefined, callback: (cols: CuratedCollection[]) => void) {
  if (!userId) {
    // Guest or not logged in: fallback to local/global
    return subscribeToCollections(callback);
  }

  const q = collection(db, 'users', userId, 'collections');
  return onSnapshot(q, async (snapshot) => {
    if (snapshot.empty) {
      // Auto seed starter collection for new user
      await autoSeedStarterCollectionForUser(userId);
      callback(getCachedCollections());
    } else {
      const items: CuratedCollection[] = [];
      const seenKeys = new Set<string>();
      let hasDuplicates = false;

      snapshot.forEach((docSnap) => {
        const col = { id: docSnap.id, ...docSnap.data() } as CuratedCollection;
        const key = col.sourceUrl 
          ? `url:${normalizeRemoteUrl(col.sourceUrl)}` 
          : `title:${(col.slug || col.title).toLowerCase().trim()}`;
        if (seenKeys.has(key)) {
          hasDuplicates = true;
        } else {
          seenKeys.add(key);
          items.push(col);
        }
      });

      if (hasDuplicates) {
        cleanupDuplicateCollectionsAndCatalog(userId).catch(console.warn);
      }

      items.sort((a, b) => (a.order || 0) - (b.order || 0) || a.title.localeCompare(b.title));
      cacheCollections(items);
      callback(items);
    }
  }, (err) => {
    console.warn('User collections listener error, using fallback:', err);
    callback(getCachedCollections());
  });
}

export function subscribeToUserCatalog(userId: string | undefined, callback: (items: MediaItem[]) => void) {
  if (!userId) {
    return subscribeToCatalog(callback);
  }

  const q = collection(db, 'users', userId, 'catalog');
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(getCachedCatalog());
    } else {
      const items: MediaItem[] = [];
      const seenItemKeys = new Set<string>();
      snapshot.forEach((docSnap) => {
        const item = { id: docSnap.id, ...docSnap.data() } as MediaItem;
        const key = item.tmdbId && item.tmdbId > 0 
          ? `tmdb:${item.type}:${item.tmdbId}`
          : `title:${item.type}:${item.title.toLowerCase().trim()}`;
        if (!seenItemKeys.has(key)) {
          seenItemKeys.add(key);
          items.push(item);
        }
      });
      cacheCatalog(items);
      callback(items);
    }
  }, (err) => {
    console.warn('User catalog listener error:', err);
    callback(getCachedCatalog());
  });
}

export function subscribeToUserSyncSources(userId: string | undefined, callback: (sources: SyncSource[]) => void) {
  if (!userId) {
    return subscribeToSyncSources(callback);
  }

  const q = collection(db, 'users', userId, 'sync_sources');
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(DEFAULT_SYNC_SOURCES);
    } else {
      const items: SyncSource[] = [];
      snapshot.forEach(docSnap => {
        items.push({ id: docSnap.id, ...docSnap.data() } as SyncSource);
      });
      callback(items);
    }
  }, (err) => {
    console.warn('User sync sources listener error:', err);
    callback(DEFAULT_SYNC_SOURCES);
  });
}

export async function createCollectionFromJson(
  userId: string,
  collectionTitle: string,
  rawJson: any,
  sourceUrl?: string,
  targetCollectionId?: string
): Promise<{ collection: CuratedCollection; seriesCount: number; moviesCount: number; episodesCount: number }> {
  const { series, movies } = parseAnimeCatJson(rawJson);

  if (series.length === 0 && movies.length === 0) {
    throw new Error("No s'han trobat sèries ni pel·lícules vàlides al contingut JSON proporcionat.");
  }

  const cleanTitle = collectionTitle.trim() || 'Nova Col·lecció';
  const slug = cleanTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `col-${Date.now()}`;
  const normalizedUrl = sourceUrl ? normalizeRemoteUrl(sourceUrl) : undefined;

  // Determine collectionId: prefer existing matching collection so it updates in-place!
  let collectionId = targetCollectionId;
  let existingColData: Partial<CuratedCollection> | null = null;

  if (userId) {
    try {
      const colsSnap = await getDocs(collection(db, 'users', userId, 'collections'));
      colsSnap.forEach((docSnap) => {
        const data = docSnap.data() as CuratedCollection;
        const colNormUrl = data.sourceUrl ? normalizeRemoteUrl(data.sourceUrl) : undefined;
        const matchesTargetId = Boolean(targetCollectionId && docSnap.id === targetCollectionId);
        const matchesUrl = Boolean(normalizedUrl && colNormUrl && normalizedUrl === colNormUrl);
        const matchesSlugOrTitle = data.slug === slug || data.title.toLowerCase().trim() === cleanTitle.toLowerCase();

        if (matchesTargetId || matchesUrl || (!collectionId && matchesSlugOrTitle)) {
          if (!collectionId || matchesTargetId || matchesUrl) {
            collectionId = docSnap.id;
            existingColData = data;
          }
        }
      });
    } catch (e) {
      console.warn('Could not query existing user collections:', e);
    }
  }

  // Fallback if still no collectionId: use deterministic slug-based ID
  if (!collectionId) {
    collectionId = `col_${slug}`;
  }

  let totalEpisodes = 0;
  series.forEach(s => {
    totalEpisodes += s.episodes?.length || 0;
  });

  // Stable deterministic item IDs: collectionId + item id/slug
  const allItems: MediaItem[] = [...series, ...movies].map((item, idx) => {
    const rawId = item.id || (item.tmdbId ? `tmdb-${item.tmdbId}` : `item-${idx}`);
    const stableId = `${collectionId}_${rawId}`;
    return {
      ...item,
      id: stableId,
      collectionId: collectionId,
      collectionName: cleanTitle,
      userId: userId || 'guest',
      updatedAt: new Date().toISOString()
    };
  });

  const nowIso = new Date().toISOString();
  const collectionData: CuratedCollection = {
    id: collectionId,
    userId: userId || 'guest',
    title: cleanTitle,
    slug: slug,
    description: typeof rawJson === 'object' && rawJson.description 
      ? rawJson.description 
      : (existingColData?.description || `Col·lecció amb ${series.length} sèries (${totalEpisodes} episodis) i ${movies.length} pel·lícules.`),
    seriesCount: series.length,
    moviesCount: movies.length,
    totalItems: allItems.length,
    sourceType: sourceUrl ? 'url_sync' : (existingColData?.sourceType || 'json_upload'),
    sourceUrl: sourceUrl || existingColData?.sourceUrl || undefined,
    lastSyncedAt: nowIso,
    order: existingColData?.order !== undefined ? existingColData.order : Date.now(),
    createdAt: existingColData?.createdAt || nowIso,
    updatedAt: nowIso,
    bannerImage: existingColData?.bannerImage || undefined,
    iconName: existingColData?.iconName || undefined
  };

  const newIdsSet = new Set(allItems.map(i => i.id));

  if (userId) {
    // 1. Update the existing collection document or write it cleanly
    await setDoc(doc(db, 'users', userId, 'collections', collectionId), sanitizeForFirestore(collectionData), { merge: true });

    // 2. Query existing items in user catalog belonging to this collection and purge items no longer in JSON
    try {
      const catalogSnap = await getDocs(collection(db, 'users', userId, 'catalog'));
      const itemsToDelete: string[] = [];

      catalogSnap.forEach((docSnap) => {
        const item = docSnap.data() as MediaItem;
        const belongsToCollection = item.collectionId === collectionId || docSnap.id.startsWith(`${collectionId}_`);
        if (belongsToCollection && !newIdsSet.has(docSnap.id)) {
          itemsToDelete.push(docSnap.id);
        }
      });

      if (itemsToDelete.length > 0) {
        const BATCH_SIZE = 40;
        for (let i = 0; i < itemsToDelete.length; i += BATCH_SIZE) {
          const chunk = itemsToDelete.slice(i, i + BATCH_SIZE);
          const batch = writeBatch(db);
          for (const docId of chunk) {
            batch.delete(doc(db, 'users', userId, 'catalog', docId));
          }
          await batch.commit();
        }
      }
    } catch (err) {
      console.warn('Error purging deleted items from user catalog:', err);
    }

    // 3. Commit all current items to user's catalog in batches
    const BATCH_SIZE = 40;
    for (let i = 0; i < allItems.length; i += BATCH_SIZE) {
      const chunk = allItems.slice(i, i + BATCH_SIZE);
      const batch = writeBatch(db);
      for (const item of chunk) {
        batch.set(doc(db, 'users', userId, 'catalog', item.id), sanitizeForFirestore(item), { merge: true });
      }
      await batch.commit();
      if (i + BATCH_SIZE < allItems.length) {
        await new Promise(r => setTimeout(r, 40));
      }
    }

    // 4. Automatically cleanup duplicate collections that may have been created in prior syncs
    cleanupDuplicateCollectionsAndCatalog(userId).catch(console.warn);
  } else {
    // Also save in global for fallback
    await setDoc(doc(db, 'collections', collectionId), sanitizeForFirestore(collectionData), { merge: true });

    // Purge removed items from global catalog
    try {
      const catalogSnap = await getDocs(collection(db, 'catalog'));
      const itemsToDelete: string[] = [];

      catalogSnap.forEach((docSnap) => {
        const item = docSnap.data() as MediaItem;
        const belongsToCollection = item.collectionId === collectionId || docSnap.id.startsWith(`${collectionId}_`);
        if (belongsToCollection && !newIdsSet.has(docSnap.id)) {
          itemsToDelete.push(docSnap.id);
        }
      });

      if (itemsToDelete.length > 0) {
        const BATCH_SIZE = 40;
        for (let i = 0; i < itemsToDelete.length; i += BATCH_SIZE) {
          const chunk = itemsToDelete.slice(i, i + BATCH_SIZE);
          const batch = writeBatch(db);
          for (const docId of chunk) {
            batch.delete(doc(db, 'catalog', docId));
          }
          await batch.commit();
        }
      }
    } catch (err) {
      console.warn('Error purging deleted items from global catalog:', err);
    }

    await commitMediaItemsInBatches(allItems);
  }

  return {
    collection: collectionData,
    seriesCount: series.length,
    moviesCount: movies.length,
    episodesCount: totalEpisodes
  };
}

/**
 * Automatically cleans up duplicate collections and redundant catalog items for a user.
 * Merges repeated collections originating from multiple syncs into a single canonical collection.
 */
export async function cleanupDuplicateCollectionsAndCatalog(userId: string): Promise<{
  deletedCollections: number;
  deletedItems: number;
}> {
  if (!userId) return { deletedCollections: 0, deletedItems: 0 };

  let deletedCollections = 0;
  let deletedItems = 0;

  try {
    const colsSnap = await getDocs(collection(db, 'users', userId, 'collections'));
    if (colsSnap.empty) return { deletedCollections: 0, deletedItems: 0 };

    const collectionsList: CuratedCollection[] = [];
    colsSnap.forEach((d) => {
      collectionsList.push({ id: d.id, ...d.data() } as CuratedCollection);
    });

    // Group collections by identity: normalized sourceUrl or slug / lowercase title
    const groups: Record<string, CuratedCollection[]> = {};
    for (const col of collectionsList) {
      const key = col.sourceUrl 
        ? `url:${normalizeRemoteUrl(col.sourceUrl)}` 
        : `title:${(col.slug || col.title).toLowerCase().trim()}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(col);
    }

    const colsToDelete: string[] = [];
    const canonicalMap = new Map<string, string>(); // duplicateColId -> canonicalColId

    for (const key of Object.keys(groups)) {
      const group = groups[key];
      if (group.length > 1) {
        // Prefer deterministic IDs (without random trailing suffix), then most recently updated
        group.sort((a, b) => {
          const aHasRandom = /_[a-z0-9]{4,6}$/.test(a.id);
          const bHasRandom = /_[a-z0-9]{4,6}$/.test(b.id);
          if (aHasRandom !== bHasRandom) return aHasRandom ? 1 : -1;
          const aTime = new Date(a.updatedAt || a.lastSyncedAt || a.createdAt || 0).getTime();
          const bTime = new Date(b.updatedAt || b.lastSyncedAt || b.createdAt || 0).getTime();
          return bTime - aTime;
        });

        const canonical = group[0];
        for (let i = 1; i < group.length; i++) {
          colsToDelete.push(group[i].id);
          canonicalMap.set(group[i].id, canonical.id);
        }
      }
    }

    // Delete duplicate collection documents
    for (const colId of colsToDelete) {
      try {
        await deleteDoc(doc(db, 'users', userId, 'collections', colId));
        deletedCollections++;
      } catch (err) {
        console.warn(`Error deleting duplicate collection ${colId}:`, err);
      }
    }

    // Delete items in user's catalog belonging to deleted duplicate collections
    if (colsToDelete.length > 0) {
      const catalogSnap = await getDocs(collection(db, 'users', userId, 'catalog'));
      const itemsToDelete: string[] = [];

      catalogSnap.forEach((docSnap) => {
        const item = docSnap.data() as MediaItem;
        const belongsToDeleted = colsToDelete.includes(item.collectionId || '') ||
          colsToDelete.some(cid => docSnap.id.startsWith(`${cid}_`));
        if (belongsToDeleted) {
          itemsToDelete.push(docSnap.id);
        }
      });

      if (itemsToDelete.length > 0) {
        const BATCH_SIZE = 40;
        for (let i = 0; i < itemsToDelete.length; i += BATCH_SIZE) {
          const chunk = itemsToDelete.slice(i, i + BATCH_SIZE);
          const batch = writeBatch(db);
          for (const docId of chunk) {
            batch.delete(doc(db, 'users', userId, 'catalog', docId));
            deletedItems++;
          }
          await batch.commit();
        }
      }

      // Also ensure user sync sources point to canonical collection IDs
      try {
        const sourcesSnap = await getDocs(collection(db, 'users', userId, 'sync_sources'));
        for (const srcDoc of sourcesSnap.docs) {
          const srcData = srcDoc.data() as SyncSource;
          if (srcData.collectionId && canonicalMap.has(srcData.collectionId)) {
            const canonicalId = canonicalMap.get(srcData.collectionId)!;
            await updateDoc(srcDoc.ref, { collectionId: canonicalId });
          }
        }
      } catch (srcErr) {
        console.warn('Error updating sync sources canonical collectionId:', srcErr);
      }
    }

    // Also deduplicate catalog items across the entire user catalog (keep 1 item per tmdbId or title+type)
    const catalogSnap = await getDocs(collection(db, 'users', userId, 'catalog'));
    const itemMap = new Map<string, string>();
    const dupItemDocs: string[] = [];

    catalogSnap.forEach((docSnap) => {
      const item = docSnap.data() as MediaItem;
      const key = item.tmdbId && item.tmdbId > 0 
        ? `tmdb_${item.type}_${item.tmdbId}`
        : `title_${item.type}_${item.title.toLowerCase().trim()}`;
      
      if (itemMap.has(key)) {
        dupItemDocs.push(docSnap.id);
      } else {
        itemMap.set(key, docSnap.id);
      }
    });

    if (dupItemDocs.length > 0) {
      const BATCH_SIZE = 40;
      for (let i = 0; i < dupItemDocs.length; i += BATCH_SIZE) {
        const chunk = dupItemDocs.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const docId of chunk) {
          batch.delete(doc(db, 'users', userId, 'catalog', docId));
          deletedItems++;
        }
        await batch.commit();
      }
    }
  } catch (err) {
    console.warn('Error during deduplication cleanup:', err);
  }

  return { deletedCollections, deletedItems };
}

export async function deleteUserCollection(userId: string, collectionId: string): Promise<void> {
  if (!userId) {
    await deleteDoc(doc(db, 'collections', collectionId));
    return;
  }

  // 1. Delete collection doc
  await deleteDoc(doc(db, 'users', userId, 'collections', collectionId));

  // 2. Delete all items in user catalog belonging to this collection
  try {
    const snap = await getDocs(collection(db, 'users', userId, 'catalog'));
    const batch = writeBatch(db);
    let count = 0;
    snap.forEach((docSnap) => {
      const data = docSnap.data() as MediaItem;
      if (data.collectionId === collectionId || docSnap.id.startsWith(`${collectionId}_`)) {
        batch.delete(docSnap.ref);
        count++;
      }
    });
    if (count > 0) {
      await batch.commit();
    }
  } catch (err) {
    console.warn('Error cleaning up collection items:', err);
  }
}

export async function saveUserMediaItem(userId: string, item: MediaItem): Promise<void> {
  const sanitized = sanitizeForFirestore({
    ...item,
    userId: userId || 'guest',
    updatedAt: new Date().toISOString()
  });

  if (userId) {
    await setDoc(doc(db, 'users', userId, 'catalog', item.id), sanitized, { merge: true });
  } else {
    await setDoc(doc(db, 'catalog', item.id), sanitized, { merge: true });
  }
}

export async function deleteUserMediaItem(userId: string, itemId: string): Promise<void> {
  if (userId) {
    await deleteDoc(doc(db, 'users', userId, 'catalog', itemId));
  } else {
    await deleteDoc(doc(db, 'catalog', itemId));
  }
}

export async function getUserSyncSources(userId?: string): Promise<SyncSource[]> {
  try {
    if (userId) {
      const snap = await getDocs(collection(db, 'users', userId, 'sync_sources'));
      if (!snap.empty) {
        return snap.docs.map(d => ({ id: d.id, ...d.data() } as SyncSource));
      }
    }
    const snap = await getDocs(collection(db, 'sync_sources'));
    if (!snap.empty) {
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as SyncSource));
    }
    return DEFAULT_SYNC_SOURCES;
  } catch {
    return DEFAULT_SYNC_SOURCES;
  }
}

export async function saveUserSyncSource(userId: string, source: SyncSource): Promise<void> {
  const normalizedUrl = normalizeRemoteUrl(source.url);
  const data = sanitizeForFirestore({
    ...source,
    url: normalizedUrl,
    userId: userId || 'guest'
  });

  if (userId) {
    await setDoc(doc(db, 'users', userId, 'sync_sources', source.id), data, { merge: true });
  } else {
    await setDoc(doc(db, 'sync_sources', source.id), data, { merge: true });
  }
}

export async function deleteUserSyncSource(userId: string, sourceId: string): Promise<void> {
  if (userId) {
    await deleteDoc(doc(db, 'users', userId, 'sync_sources', sourceId));
  } else {
    await deleteDoc(doc(db, 'sync_sources', sourceId));
  }
}

export async function syncUserSourceFromUrl(
  userId: string,
  sourceOrUrl: SyncSource | string,
  targetCollectionTitle?: string
): Promise<SyncResult> {
  const isObject = typeof sourceOrUrl === 'object';
  const url = isObject ? sourceOrUrl.url : sourceOrUrl;
  const sourceId = isObject ? sourceOrUrl.id : `src_${Date.now()}`;
  const sourceName = isObject ? sourceOrUrl.name : (targetCollectionTitle || url);
  const targetCollectionId = isObject ? sourceOrUrl.collectionId : undefined;

  if (userId && isObject) {
    try {
      await updateDoc(doc(db, 'users', userId, 'sync_sources', sourceId), { status: 'syncing' });
    } catch {}
  }

  try {
    const { data } = await fetchRemoteJson(url);
    const colName = targetCollectionTitle || sourceName || (typeof data === 'object' && data.name ? data.name : 'Col·lecció Sincronitzada');

    // Update the existing collection in place instead of creating a new duplicate collection
    const result = await createCollectionFromJson(userId, colName, data, url, targetCollectionId);

    const timestamp = new Date().toISOString();
    const totalItems = result.seriesCount + result.moviesCount;

    if (userId) {
      await setDoc(doc(db, 'users', userId, 'sync_sources', sourceId), sanitizeForFirestore({
        id: sourceId,
        name: sourceName,
        url,
        lastSyncedAt: timestamp,
        itemCount: totalItems,
        status: 'success',
        collectionId: result.collection.id,
        createdAt: isObject && sourceOrUrl.createdAt ? sourceOrUrl.createdAt : timestamp
      }), { merge: true });
    }

    return {
      sourceId,
      sourceName,
      success: true,
      seriesCount: result.seriesCount,
      moviesCount: result.moviesCount,
      episodesCount: result.episodesCount,
      message: `Col·lecció '${colName}' actualitzada amb èxit: ${result.seriesCount} sèries (${result.episodesCount} episodis) i ${result.moviesCount} pel·lícules.`,
      timestamp
    };
  } catch (error: any) {
    const errorMsg = error.message || 'Error desconegut durant la sincronització';
    if (userId && isObject) {
      try {
        await updateDoc(doc(db, 'users', userId, 'sync_sources', sourceId), {
          status: 'error',
          lastError: errorMsg
        });
      } catch {}
    }

    return {
      sourceId,
      sourceName,
      success: false,
      seriesCount: 0,
      moviesCount: 0,
      episodesCount: 0,
      message: `Error en sincronitzar '${sourceName}': ${errorMsg}`,
      timestamp: new Date().toISOString()
    };
  }
}

export async function initializeDatabaseIfNeeded(): Promise<void> {
  try {
    const catalogSnap = await getDocs(collection(db, 'catalog'));
    if (catalogSnap.empty) {
      console.log('Seeding initial AnimeCat catalog into database animecat...');
      const batch = writeBatch(db);

      INITIAL_MEDIA_ITEMS.forEach(item => {
        const itemRef = doc(db, 'catalog', item.id);
        batch.set(itemRef, sanitizeForFirestore(item));
      });

      INITIAL_COLLECTIONS.forEach(col => {
        const colRef = doc(db, 'collections', col.id);
        batch.set(colRef, sanitizeForFirestore(col));
      });

      DEFAULT_SYNC_SOURCES.forEach(src => {
        const srcRef = doc(db, 'sync_sources', src.id);
        batch.set(srcRef, sanitizeForFirestore(src));
      });

      await batch.commit();
      console.log('Initial catalog and sources seeded successfully!');
    }
  } catch (err) {
    console.error('Error initializing database:', err);
  }
}

export async function getCatalogDirect(): Promise<MediaItem[]> {
  try {
    const snap = await getDocs(collection(db, 'catalog'));
    if (snap.empty) {
      return getCachedCatalog();
    }
    const items: MediaItem[] = [];
    snap.forEach((docSnap) => {
      items.push({ id: docSnap.id, ...docSnap.data() } as MediaItem);
    });
    cacheCatalog(items);
    return items;
  } catch (err) {
    console.warn('Direct catalog fetch error:', err);
    return getCachedCatalog();
  }
}

export function subscribeToCatalog(callback: (items: MediaItem[]) => void) {
  const q = collection(db, 'catalog');
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      const fallback = getCachedCatalog();
      callback(fallback);
      // Auto seed in background if empty
      initializeDatabaseIfNeeded();
    } else {
      const items: MediaItem[] = [];
      snapshot.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...docSnap.data() } as MediaItem);
      });
      cacheCatalog(items);
      callback(items);
    }
  }, (err) => {
    console.warn('Firestore catalog listener error, using cached data:', err);
    callback(getCachedCatalog());
  });
}

export function subscribeToCollections(callback: (collections: CuratedCollection[]) => void) {
  const q = collection(db, 'collections');
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      const fallback = getCachedCollections();
      callback(fallback);
    } else {
      const items: CuratedCollection[] = [];
      snapshot.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...docSnap.data() } as CuratedCollection);
      });
      items.sort((a, b) => (a.order || 0) - (b.order || 0));
      cacheCollections(items);
      callback(items);
    }
  }, (err) => {
    console.warn('Firestore collections listener error:', err);
    callback(getCachedCollections());
  });
}

export async function saveMediaItem(item: MediaItem): Promise<void> {
  const docRef = doc(db, 'catalog', item.id);
  const sanitized = sanitizeForFirestore({
    ...item,
    updatedAt: new Date().toISOString()
  });
  await setDoc(docRef, sanitized, { merge: true });
}

export async function deleteMediaItem(itemId: string): Promise<void> {
  await deleteDoc(doc(db, 'catalog', itemId));
}

export async function saveCollection(collectionData: CuratedCollection): Promise<void> {
  const sanitized = sanitizeForFirestore(collectionData);
  if (collectionData.userId && collectionData.userId !== 'guest') {
    await setDoc(doc(db, 'users', collectionData.userId, 'collections', collectionData.id), sanitized, { merge: true });
  }
  await setDoc(doc(db, 'collections', collectionData.id), sanitized, { merge: true });
}

export async function deleteCollection(collectionId: string): Promise<void> {
  await deleteDoc(doc(db, 'collections', collectionId));
}

// ----------------------------------------------------
// SANITIZE HELPER (Strips all undefined values for Firestore)
// ----------------------------------------------------

export function sanitizeForFirestore<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return null as any;
  }
  if (Array.isArray(obj)) {
    return obj
      .filter(item => item !== undefined)
      .map(item => sanitizeForFirestore(item)) as any;
  }
  if (typeof obj === 'object' && !(obj instanceof Date)) {
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = sanitizeForFirestore(value);
      }
    }
    return cleaned as T;
  }
  return obj;
}

// ----------------------------------------------------
// JSON IMPORT & EXPORT ENGINE
// ----------------------------------------------------

async function commitMediaItemsInBatches(items: MediaItem[]): Promise<void> {
  const BATCH_SIZE = 40;
  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    const chunk = items.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);
    for (const item of chunk) {
      const sanitized = sanitizeForFirestore({
        ...item,
        updatedAt: new Date().toISOString()
      });
      batch.set(doc(db, 'catalog', item.id), sanitized, { merge: true });
    }
    await batch.commit();
    if (i + BATCH_SIZE < items.length) {
      await new Promise(resolve => setTimeout(resolve, 80));
    }
  }
}

function normalizeEpisode(ep: any, index: number): EpisodeInfo {
  const epObj: EpisodeInfo = {
    season: typeof ep.season === 'number' ? ep.season : (typeof ep.season_number === 'number' ? ep.season_number : 1),
    episode: typeof ep.episode === 'number' ? ep.episode : (typeof ep.episode_number === 'number' ? ep.episode_number : (typeof ep.number === 'number' ? ep.number : index + 1)),
    title: ep.title || ep.name || ep.titol || `Episodi ${ep.episode || ep.episode_number || index + 1}`,
    plot: ep.plot || ep.summary || ep.description || ep.overview || '',
    poster: ep.poster || ep.poster_path || ep.image || ep.thumbnail || '',
    stream_url: ep.stream_url || ep.streamUrl || ep.url || ep.video_url || ep.videoUrl || ep.link || ''
  };

  const tmdb = typeof ep.tmdb_id === 'number' ? ep.tmdb_id : (typeof ep.tmdbId === 'number' ? ep.tmdbId : undefined);
  if (tmdb !== undefined) epObj.tmdb_id = tmdb;

  const airDate = ep.air_date || ep.airDate || ep.release_date;
  if (airDate) epObj.air_date = airDate;

  const runtime = typeof ep.runtime === 'number' ? ep.runtime : undefined;
  if (runtime !== undefined) epObj.runtime = runtime;

  const rating = typeof ep.rating === 'number' ? ep.rating : (typeof ep.vote_average === 'number' ? ep.vote_average : undefined);
  if (rating !== undefined) epObj.rating = rating;

  return epObj;
}

export function parseAnimeCatJson(rawJson: any): { series: MediaItem[], movies: MediaItem[] } {
  let parsed: any;
  if (typeof rawJson === 'string') {
    try {
      const clean = rawJson.replace(/^\uFEFF/, '').trim();
      parsed = JSON.parse(clean);
    } catch {
      const sanitized = rawJson
        .replace(/^\uFEFF/, '')
        .replace(/\/\*[\s\S]*?\*\/|([^:]|^)\/\/.*$/gm, '$1')
        .replace(/,\s*([}\]])/g, '$1')
        .trim();
      parsed = JSON.parse(sanitized);
    }
  } else {
    parsed = rawJson;
  }

  if (!parsed || typeof parsed !== 'object') {
    return { series: [], movies: [] };
  }

  const seriesItems: MediaItem[] = [];
  const movieItems: MediaItem[] = [];
  const generatedAt = parsed.generated_at || new Date().toISOString();
  const existingIds = new Set<string>();

  // Helper to ensure unique document ID
  const makeUniqueId = (baseSlug: string): string => {
    let candidate = baseSlug;
    let count = 1;
    while (existingIds.has(candidate)) {
      candidate = `${baseSlug}-${count++}`;
    }
    existingIds.add(candidate);
    return candidate;
  };

  // Helper to process a potential series item
  const processSeriesItem = (sData: any, episodesData?: any[], fallbackIndex: number = 0) => {
    if (!sData) return;
    const name = sData.name || sData.title || sData.nom || '';
    if (!name) return;

    const tmdb = sData.tmdb_id || sData.tmdbId || sData.id || fallbackIndex;
    const baseSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `series-${tmdb}`;
    const slug = makeUniqueId(baseSlug);
    
    const rawEpisodes = Array.isArray(episodesData) ? episodesData : (Array.isArray(sData.episodes) ? sData.episodes : []);
    const episodes: EpisodeInfo[] = rawEpisodes.map((ep, idx) => normalizeEpisode(ep, idx));

    const rawGenres = Array.isArray(sData.genres) 
      ? sData.genres 
      : (typeof sData.genres === 'string' ? sData.genres.split(',').map((g: string) => g.trim()) : []);
    const genres = cleanGenres(rawGenres);
    if (genres.length === 0) genres.push('Acció i Aventura');

    seriesItems.push({
      id: slug,
      type: 'series',
      title: name,
      originalTitle: sData.original_name || sData.original_title || sData.titol_original || '',
      summary: sData.summary || sData.plot || sData.description || sData.overview || sData.sinopsi || '',
      poster: sData.poster || sData.poster_path || sData.image || '',
      backdrop: sData.backdrop || sData.backdrop_path || sData.banner || '',
      genres,
      tmdbId: typeof sData.tmdb_id === 'number' ? sData.tmdb_id : (typeof sData.tmdbId === 'number' ? sData.tmdbId : 0),
      year: sData.first_air_date ? String(sData.first_air_date).split('-')[0] : (sData.year ? String(sData.year) : ''),
      status: sData.status || 'Finalitzada',
      numberOfSeasons: sData.number_of_seasons || sData.numberOfSeasons || 1,
      numberOfEpisodes: sData.number_of_episodes || sData.numberOfEpisodes || episodes.length || 0,
      episodes,
      audioLanguage: sData.audioLanguage || sData.audio || sData.language || undefined,
      collections: sData.collections || ['classics-3xl'],
      addedAt: sData.addedAt || generatedAt,
      updatedAt: sData.updatedAt || new Date().toISOString()
    });
  };

  // Helper to process a potential movie item
  const processMovieItem = (mData: any, fallbackIndex: number = 0) => {
    if (!mData) return;
    const title = mData.title || mData.name || mData.nom || '';
    if (!title) return;

    const tmdb = mData.tmdb_id || mData.tmdbId || mData.id || fallbackIndex;
    const baseSlug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || `movie-${tmdb}`;
    const slug = makeUniqueId(baseSlug);

    const rawGenres = Array.isArray(mData.genres) 
      ? mData.genres 
      : (typeof mData.genres === 'string' ? mData.genres.split(',').map((g: string) => g.trim()) : []);
    const genres = cleanGenres(rawGenres);
    if (genres.length === 0) genres.push('Cinema');

    const movieObj: MediaItem = {
      id: slug,
      type: 'movie',
      title,
      originalTitle: mData.original_title || mData.original_name || mData.titol_original || '',
      summary: mData.plot || mData.summary || mData.description || mData.overview || mData.sinopsi || '',
      poster: mData.poster || mData.poster_path || mData.image || '',
      backdrop: mData.backdrop || mData.backdrop_path || mData.banner || '',
      genres,
      tmdbId: typeof mData.tmdb_id === 'number' ? mData.tmdb_id : (typeof mData.tmdbId === 'number' ? mData.tmdbId : 0),
      year: mData.release_date ? String(mData.release_date).split('-')[0] : (mData.year ? String(mData.year) : ''),
      streamUrl: mData.stream_url || mData.streamUrl || mData.url || mData.video_url || mData.link || '',
      audioLanguage: mData.audioLanguage || mData.audio || mData.language || undefined,
      collections: mData.collections || ['pellicules-nostalgia'],
      addedAt: mData.addedAt || generatedAt,
      updatedAt: mData.updatedAt || new Date().toISOString()
    };

    if (typeof mData.rating === 'number') {
      movieObj.rating = mData.rating;
    } else if (typeof mData.vote_average === 'number') {
      movieObj.rating = mData.vote_average;
    }

    if (typeof mData.runtime === 'number') {
      movieObj.runtime = mData.runtime;
    }

    movieItems.push(movieObj);
  };

  // Case A: Root is an Array
  if (Array.isArray(parsed)) {
    parsed.forEach((item: any, idx: number) => {
      if (item.series || (Array.isArray(item.episodes) && item.episodes.length > 0)) {
        processSeriesItem(item.series || item, item.episodes, idx);
      } else if (item.type === 'series') {
        processSeriesItem(item, item.episodes, idx);
      } else if (item.type === 'movie' || item.movie || item.stream_url || item.streamUrl) {
        processMovieItem(item.movie || item, idx);
      } else if (item.name && !item.title) {
        processSeriesItem(item, item.episodes, idx);
      } else {
        processMovieItem(item, idx);
      }
    });
  }

  // Case B: Standard series_list array
  const rawSeriesList = parsed.series_list || parsed.seriesList || parsed.animes || (Array.isArray(parsed.series) ? parsed.series : undefined);
  if (Array.isArray(rawSeriesList)) {
    rawSeriesList.forEach((item: any, index: number) => {
      if (item.series) {
        processSeriesItem(item.series, item.episodes, index);
      } else {
        processSeriesItem(item, item.episodes, index);
      }
    });
  }

  // Case C: Standard movie_list array
  const rawMovieList = parsed.movie_list || parsed.movieList || parsed.films || parsed.pelicules || (Array.isArray(parsed.movies) ? parsed.movies : undefined);
  if (Array.isArray(rawMovieList)) {
    rawMovieList.forEach((item: any, index: number) => {
      processMovieItem(item.movie || item, index);
    });
  }

  // Case D: Generic data / catalog / items / results array
  const genericList = parsed.data || parsed.catalog || parsed.items || parsed.results;
  if (Array.isArray(genericList)) {
    genericList.forEach((item: any, index: number) => {
      if (item.series || (Array.isArray(item.episodes) && item.episodes.length > 0) || item.type === 'series') {
        processSeriesItem(item.series || item, item.episodes, index);
      } else {
        processMovieItem(item.movie || item, index);
      }
    });
  }

  // Case E: Single series object
  if (parsed.series && typeof parsed.series === 'object' && !Array.isArray(parsed.series)) {
    processSeriesItem(parsed.series, parsed.episodes, 0);
  }

  // Case F: Single movie object
  if (parsed.movie && typeof parsed.movie === 'object' && !Array.isArray(parsed.movie)) {
    processMovieItem(parsed.movie, 0);
  }

  // Case G: Single series or movie at root (unwrapped)
  if (!Array.isArray(parsed) && !parsed.series_list && !parsed.movie_list && !parsed.series && !parsed.movie && !parsed.data) {
    if (Array.isArray(parsed.episodes) && (parsed.name || parsed.title)) {
      processSeriesItem(parsed, parsed.episodes, 0);
    } else if (parsed.stream_url || parsed.streamUrl || parsed.plot) {
      processMovieItem(parsed, 0);
    } else if (parsed.name || parsed.title) {
      if (parsed.type === 'movie') {
        processMovieItem(parsed, 0);
      } else {
        processSeriesItem(parsed, parsed.episodes, 0);
      }
    }
  }

  return { series: seriesItems, movies: movieItems };
}

export async function importJsonToFirestore(jsonData: AnimeCatJsonRoot | string): Promise<{ seriesCount: number, moviesCount: number }> {
  const { series, movies } = parseAnimeCatJson(jsonData);
  const allItems = [...series, ...movies];
  await commitMediaItemsInBatches(allItems);
  return { seriesCount: series.length, moviesCount: movies.length };
}

export async function exportCatalogToJson(): Promise<AnimeCatJsonRoot> {
  const snapshot = await getDocs(collection(db, 'catalog'));
  const seriesList: SeriesListItem[] = [];
  const movieList: MovieInfo[] = [];

  snapshot.forEach((docSnap) => {
    const item = docSnap.data() as MediaItem;
    if (item.type === 'series') {
      seriesList.push({
        series: {
          tmdb_id: item.tmdbId,
          name: item.title,
          original_name: item.originalTitle,
          summary: item.summary,
          poster: item.poster,
          backdrop: item.backdrop,
          genres: item.genres,
          status: item.status,
          first_air_date: item.year ? `${item.year}-01-01` : undefined,
          number_of_seasons: item.numberOfSeasons || 1,
          number_of_episodes: item.numberOfEpisodes || item.episodes?.length || 0
        },
        episodes: item.episodes || []
      });
    } else {
      movieList.push({
        tmdb_id: item.tmdbId,
        title: item.title,
        original_title: item.originalTitle,
        plot: item.summary,
        poster: item.poster,
        backdrop: item.backdrop,
        runtime: item.runtime,
        rating: item.rating,
        stream_url: item.streamUrl || '',
        genres: item.genres
      });
    }
  });

  return {
    generated_at: new Date().toISOString(),
    series: null,
    episodes: [],
    movie: null,
    series_list: seriesList,
    movie_list: movieList
  };
}

// ----------------------------------------------------
// WATCHLIST & WATCH HISTORY
// ----------------------------------------------------

export function subscribeToWatchlist(userId: string, callback: (items: WatchlistItem[]) => void) {
  if (!userId) {
    callback([]);
    return () => {};
  }
  const q = collection(db, 'users', userId, 'watchlist');
  return onSnapshot(q, (snapshot) => {
    const items: WatchlistItem[] = [];
    snapshot.forEach(docSnap => {
      items.push({ id: docSnap.id, ...docSnap.data() } as WatchlistItem);
    });
    callback(items);
  }, (err) => {
    console.warn('Watchlist listener error:', err);
    callback([]);
  });
}

export async function toggleWatchlist(userId: string, item: MediaItem, isInList: boolean): Promise<boolean> {
  if (!userId) return false;
  const docRef = doc(db, 'users', userId, 'watchlist', item.id);
  if (isInList) {
    await deleteDoc(docRef);
    return false;
  } else {
    const watchlistItem: WatchlistItem = {
      id: item.id,
      mediaId: item.id,
      type: item.type,
      title: item.title,
      poster: item.poster,
      backdrop: item.backdrop,
      genres: item.genres,
      rating: item.rating,
      addedAt: new Date().toISOString()
    };
    await setDoc(docRef, sanitizeForFirestore(watchlistItem));
    return true;
  }
}

export function subscribeToWatchHistory(userId: string, callback: (items: WatchHistoryItem[]) => void) {
  if (!userId) {
    callback([]);
    return () => {};
  }
  const q = collection(db, 'users', userId, 'history');
  return onSnapshot(q, (snapshot) => {
    const items: WatchHistoryItem[] = [];
    snapshot.forEach(docSnap => {
      items.push({ id: docSnap.id, ...docSnap.data() } as WatchHistoryItem);
    });
    items.sort((a, b) => new Date(b.lastWatchedAt).getTime() - new Date(a.lastWatchedAt).getTime());
    callback(items);
  }, (err) => {
    console.warn('Watch history listener error:', err);
    callback([]);
  });
}

export async function updateWatchProgress(
  userId: string,
  media: MediaItem,
  progressSeconds: number,
  durationSeconds: number,
  episode?: { season: number; episode: number; title: string; stream_url: string },
  forceCompleted?: boolean
): Promise<void> {
  const historyId = episode 
    ? `${media.id}_s${episode.season}_e${episode.episode}`
    : `${media.id}_movie`;

  const percentage = durationSeconds > 0 ? Math.round((progressSeconds / durationSeconds) * 100) : 0;
  const isFinished = forceCompleted !== undefined ? forceCompleted : percentage >= 90;

  const historyItem: WatchHistoryItem = {
    id: historyId,
    mediaId: media.id,
    mediaTitle: media.title,
    mediaPoster: episode?.stream_url ? (media.episodes?.find(e => e.episode === episode.episode)?.poster || media.poster) : media.poster,
    type: media.type,
    tmdbId: media.tmdbId,
    season: episode?.season,
    episode: episode?.episode,
    episodeTitle: episode?.title,
    streamUrl: episode?.stream_url || media.streamUrl || '',
    progressSeconds: Math.round(progressSeconds),
    durationSeconds: Math.round(durationSeconds),
    percentage,
    completed: isFinished,
    lastWatchedAt: new Date().toISOString()
  };

  // Always mirror to localStorage for offline and instant UI updates
  try {
    const saved = localStorage.getItem('garcollections_local_watch_history');
    let list: WatchHistoryItem[] = saved ? JSON.parse(saved) : [];
    if (!Array.isArray(list)) list = [];
    const idx = list.findIndex(h => h.id === historyId || h.mediaId === media.id);
    if (idx >= 0) {
      list[idx] = historyItem;
    } else {
      list.unshift(historyItem);
    }
    localStorage.setItem('garcollections_local_watch_history', JSON.stringify(list.slice(0, 50)));
  } catch {}

  if (!userId) return;

  await setDoc(doc(db, 'users', userId, 'history', historyId), sanitizeForFirestore(historyItem), { merge: true });
}

export async function deleteWatchHistoryItem(userId: string, historyId: string): Promise<void> {
  if (!userId || !historyId) return;
  await deleteDoc(doc(db, 'users', userId, 'history', historyId));
}

// ----------------------------------------------------
// SYNC SOURCES & REMOTE SYNCHRONIZATION
// ----------------------------------------------------

export const DEFAULT_SYNC_SOURCES: SyncSource[] = [
  {
    id: 'source-backblaze-movies',
    name: 'Pel·lícules AnimeCat (Backblaze B2)',
    url: 'https://f003.backblazeb2.com/file/garcomedia/collections/animecat-movies.json',
    lastSyncedAt: new Date().toISOString(),
    itemCount: 315,
    status: 'idle',
    createdAt: new Date().toISOString()
  }
];

export function subscribeToSyncSources(callback: (sources: SyncSource[]) => void) {
  const q = collection(db, 'sync_sources');
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(DEFAULT_SYNC_SOURCES);
    } else {
      const items: SyncSource[] = [];
      snapshot.forEach(docSnap => {
        items.push({ id: docSnap.id, ...docSnap.data() } as SyncSource);
      });
      callback(items);
    }
  }, (err) => {
    console.warn('Sync sources listener error, fallback to default:', err);
    callback(DEFAULT_SYNC_SOURCES);
  });
}

export async function saveSyncSource(source: SyncSource): Promise<void> {
  const normalizedUrl = normalizeRemoteUrl(source.url);
  const docRef = doc(db, 'sync_sources', source.id);
  await setDoc(docRef, sanitizeForFirestore({
    ...source,
    url: normalizedUrl
  }), { merge: true });
}

export async function deleteSyncSource(sourceId: string): Promise<void> {
  await deleteDoc(doc(db, 'sync_sources', sourceId));
}

/**
 * Synchronizes a specific remote URL or registered SyncSource with Firestore
 */
export async function syncSourceFromUrl(sourceOrUrl: SyncSource | string): Promise<SyncResult> {
  const isObject = typeof sourceOrUrl === 'object';
  const url = isObject ? sourceOrUrl.url : sourceOrUrl;
  const sourceId = isObject ? sourceOrUrl.id : undefined;
  const sourceName = isObject ? sourceOrUrl.name : url;

  if (sourceId) {
    // Set status to syncing in firestore
    try {
      await updateDoc(doc(db, 'sync_sources', sourceId), { status: 'syncing' });
    } catch {
      // ignore
    }
  }

  try {
    const { data } = await fetchRemoteJson(url);
    const { series, movies } = parseAnimeCatJson(data);

    if (series.length === 0 && movies.length === 0) {
      throw new Error("No s'han trobat sèries ni pel·lícules vàlides al fitxer JSON remot.");
    }

    const allItems = [...series, ...movies];
    let totalEpisodes = 0;
    series.forEach(item => {
      totalEpisodes += item.episodes?.length || 0;
    });

    await commitMediaItemsInBatches(allItems);

    const timestamp = new Date().toISOString();
    const totalItems = series.length + movies.length;

    if (sourceId) {
      try {
        await updateDoc(doc(db, 'sync_sources', sourceId), {
          lastSyncedAt: timestamp,
          itemCount: totalItems,
          status: 'success',
          lastError: null
        });
      } catch (e) {
        // If doc didn't exist yet
        await setDoc(doc(db, 'sync_sources', sourceId), {
          id: sourceId,
          name: sourceName,
          url,
          lastSyncedAt: timestamp,
          itemCount: totalItems,
          status: 'success',
          createdAt: timestamp
        });
      }
    }

    return {
      sourceId,
      sourceName,
      success: true,
      seriesCount: series.length,
      moviesCount: movies.length,
      episodesCount: totalEpisodes,
      message: `Sincronitzat amb èxit: ${series.length} sèries (${totalEpisodes} episodis) i ${movies.length} pel·lícules actualitzades a la base de dades.`,
      timestamp
    };
  } catch (error: any) {
    const errorMsg = error.message || 'Error desconegut durant la sincronització';
    if (sourceId) {
      try {
        await updateDoc(doc(db, 'sync_sources', sourceId), {
          status: 'error',
          lastError: errorMsg
        });
      } catch {
        // ignore
      }
    }

    return {
      sourceId,
      sourceName,
      success: false,
      seriesCount: 0,
      moviesCount: 0,
      episodesCount: 0,
      message: `Error en sincronitzar '${sourceName}': ${errorMsg}`,
      timestamp: new Date().toISOString()
    };
  }
}

/**
 * Synchronizes all registered sources
 */
export async function syncAllRegisteredSources(sources: SyncSource[]): Promise<SyncResult[]> {
  const results: SyncResult[] = [];
  for (const src of sources) {
    const res = await syncSourceFromUrl(src);
    results.push(res);
  }
  return results;
}

export interface StartupSyncProgress {
  stage: string;
  count?: number;
  syncedSources?: number;
  totalSources?: number;
  done?: boolean;
}

export async function getUserCatalogDirect(userId?: string): Promise<MediaItem[]> {
  if (!userId) return getCatalogDirect();
  try {
    const snap = await getDocs(collection(db, 'users', userId, 'catalog'));
    if (snap.empty) {
      return [];
    }
    const items: MediaItem[] = [];
    snap.forEach((docSnap) => {
      items.push({ id: docSnap.id, ...docSnap.data() } as MediaItem);
    });
    return items;
  } catch (err) {
    console.warn('Direct user catalog fetch error:', err);
    return [];
  }
}

export async function getUserCollectionsDirect(userId?: string): Promise<CuratedCollection[]> {
  if (!userId) {
    try {
      const snap = await getDocs(collection(db, 'collections'));
      const items: CuratedCollection[] = [];
      snap.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...docSnap.data() } as CuratedCollection);
      });
      return items;
    } catch {
      return getCachedCollections();
    }
  }
  try {
    const snap = await getDocs(collection(db, 'users', userId, 'collections'));
    const items: CuratedCollection[] = [];
    snap.forEach((docSnap) => {
      items.push({ id: docSnap.id, ...docSnap.data() } as CuratedCollection);
    });
    return items;
  } catch (err) {
    console.warn('Direct user collections fetch error:', err);
    return [];
  }
}

export { getUserCollectionsDirect as getUserCollections };

export async function exportUserCatalogToJson(
  userId?: string,
  providedCatalog?: MediaItem[],
  providedCollections?: CuratedCollection[]
): Promise<any> {
  const userCatalog = providedCatalog || (await getUserCatalogDirect(userId));
  const userCollections = providedCollections || (await getUserCollectionsDirect(userId));

  const series = userCatalog.filter(i => i.type === 'series').map(s => ({
    title: s.title,
    original_title: s.originalTitle || '',
    cover_image: s.poster,
    poster: s.poster,
    banner: s.backdrop,
    backdrop: s.backdrop,
    plot: s.summary,
    summary: s.summary,
    genres: s.genres || [],
    release_date: s.year || '2024',
    year: s.year || '2024',
    rating: s.rating || 0,
    episodes: s.episodes || []
  }));

  const movies = userCatalog.filter(i => i.type === 'movie').map(m => ({
    title: m.title,
    original_title: m.originalTitle || '',
    cover_image: m.poster,
    poster: m.poster,
    banner: m.backdrop,
    backdrop: m.backdrop,
    plot: m.summary,
    summary: m.summary,
    genres: m.genres || [],
    release_date: m.year || '2024',
    year: m.year || '2024',
    rating: m.rating || 0,
    stream_url: m.streamUrl || '',
    duration: m.runtime || 90
  }));

  return {
    name: "GarCollections User Export",
    description: "Catàleg i col·leccions exportades de l'usuari",
    collections: userCollections,
    series: series,
    movies: movies
  };
}

/**
 * Automatically synchronizes database catalogs and remote sources upon startup or manual trigger.
 */
export async function autoSyncOnStartup(
  userIdOrProgress?: string | ((progress: StartupSyncProgress) => void),
  maybeProgress?: (progress: StartupSyncProgress) => void,
  forceRemoteSync: boolean = false
): Promise<{ success: boolean; totalItems: number; syncedCount: number }> {
  const userId = typeof userIdOrProgress === 'string' ? userIdOrProgress : undefined;
  const onProgress = typeof userIdOrProgress === 'function' ? userIdOrProgress : maybeProgress;

  try {
    onProgress?.({ stage: 'Carregant col·leccions...', done: false });
    
    if (userId) {
      await cleanupDuplicateCollectionsAndCatalog(userId);

      // If user explicitly requested remote sync or if catalog is completely empty
      const currentSnap = await getDocs(collection(db, 'users', userId, 'catalog'));
      const shouldSyncRemote = forceRemoteSync || currentSnap.empty;

      if (shouldSyncRemote) {
        onProgress?.({ stage: 'Sincronitzant fonts remotes...', done: false });
        let sources = await getUserSyncSources(userId);
        if (!sources || sources.length === 0) {
          sources = DEFAULT_SYNC_SOURCES;
        }

        let syncedTotal = 0;
        for (const source of sources) {
          try {
            onProgress?.({ stage: `Sincronitzant ${source.name}...`, done: false });
            const res = await syncUserSourceFromUrl(userId, source);
            if (res.success) {
              syncedTotal += (res.seriesCount + res.moviesCount);
            }
          } catch (srcErr) {
            console.warn(`Error syncing source ${source.name}:`, srcErr);
          }
        }

        const freshSnap = await getDocs(collection(db, 'users', userId, 'catalog'));
        const totalItems = freshSnap.size;
        onProgress?.({ 
          stage: `Sincronització completada (${totalItems} títols)`, 
          count: totalItems, 
          done: true 
        });

        if (typeof window !== 'undefined' && (window as any).Android?.showToast) {
          (window as any).Android.showToast(`Sincronització completada: ${totalItems} títols`);
        }

        return { 
          success: true, 
          totalItems, 
          syncedCount: syncedTotal 
        };
      }

      const totalItems = currentSnap.size;
      onProgress?.({ 
        stage: `Catàleg al dia (${totalItems} títols)`, 
        count: totalItems, 
        done: true 
      });
      return { 
        success: true, 
        totalItems, 
        syncedCount: 0 
      };
    }

    const snap = await getDocs(collection(db, 'catalog'));
    if (snap.empty) {
      onProgress?.({ stage: 'Carregant catàleg inicial...', done: false });
      await initializeDatabaseIfNeeded();
      const initialSnap = await getDocs(collection(db, 'catalog'));
      const totalCount = initialSnap.size || INITIAL_MEDIA_ITEMS.length;
      onProgress?.({ stage: `Catàleg preparat (${totalCount} títols)`, count: totalCount, done: true });
      return { success: true, totalItems: totalCount, syncedCount: totalCount };
    }

    const totalItems = snap.size;
    onProgress?.({ 
      stage: `Catàleg al dia (${totalItems} títols)`, 
      count: totalItems, 
      done: true 
    });

    return { 
      success: true, 
      totalItems, 
      syncedCount: 0 
    };
  } catch (err) {
    console.warn('Auto-sync on startup warning:', err);
    onProgress?.({ stage: 'Catàleg preparat', count: 0, done: true });
    return { success: false, totalItems: 0, syncedCount: 0 };
  }
}

// ----------------------------------------------------
// IPTV PLAYLISTS & XTREAM CODES SERVICES
// ----------------------------------------------------

export const DEFAULT_IPTV_SOURCES: IptvSource[] = [
  {
    id: 'iptv_source_cat_tdt',
    name: 'TDT Catalunya & Espanya (Emissions Oficials)',
    type: 'm3u',
    url: 'https://raw.githubusercontent.com/LaQuay/TDTChannels/master/m3u8/tv.m3u8',
    userAgent: 'IPTVSmartersPro',
    enabled: true,
    createdAt: '2025-01-01T00:00:00.000Z',
    status: 'active'
  }
];

const IPTV_SOURCES_LOCAL_KEY = 'garcollections_iptv_sources_v1';
const IPTV_FAVORITES_LOCAL_KEY = 'garcollections_iptv_favorites_v1';

export function getLocalIptvSources(): IptvSource[] {
  try {
    const raw = localStorage.getItem(IPTV_SOURCES_LOCAL_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return DEFAULT_IPTV_SOURCES;
}

export function saveLocalIptvSources(sources: IptvSource[]) {
  try {
    localStorage.setItem(IPTV_SOURCES_LOCAL_KEY, JSON.stringify(sources));
  } catch {}
}

/**
 * Subscribe to IPTV Sources (Firestore with offline localStorage fallback)
 */
export function subscribeToIptvSources(
  userId: string | undefined,
  onSourcesChange: (sources: IptvSource[]) => void
): () => void {
  let isUnsubscribed = false;

  // Initial local state delivery
  const localList = getLocalIptvSources();
  onSourcesChange(localList);

  try {
    const sourcesCol = collection(db, 'iptv_sources');
    const unsubscribe = onSnapshot(
      sourcesCol,
      (snapshot) => {
        if (isUnsubscribed) return;
        if (snapshot.empty) {
          onSourcesChange(localList);
          return;
        }

        const sources = snapshot.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        })) as IptvSource[];

        saveLocalIptvSources(sources);
        onSourcesChange(sources);
      },
      (error) => {
        console.warn('Firestore IPTV sources listener note:', error.message);
        if (!isUnsubscribed) {
          onSourcesChange(getLocalIptvSources());
        }
      }
    );

    return () => {
      isUnsubscribed = true;
      unsubscribe();
    };
  } catch (err) {
    console.warn('Failed to subscribe to IPTV sources:', err);
    return () => {
      isUnsubscribed = true;
    };
  }
}

/**
 * Save or update an IPTV Source (Firestore + Local)
 */
export async function saveIptvSource(source: IptvSource, userId?: string): Promise<void> {
  const cleanSource: IptvSource = {
    ...source,
    id: source.id || `iptv_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
    userAgent: (source.userAgent || 'IPTVSmartersPro').trim(),
    createdAt: source.createdAt || new Date().toISOString()
  };

  // Update local storage
  const current = getLocalIptvSources();
  const index = current.findIndex(s => s.id === cleanSource.id);
  if (index >= 0) {
    current[index] = cleanSource;
  } else {
    current.push(cleanSource);
  }
  saveLocalIptvSources(current);

  try {
    const docRef = doc(db, 'iptv_sources', cleanSource.id);
    await setDoc(docRef, sanitizeForFirestore(cleanSource), { merge: true });

    if (userId) {
      const userDocRef = doc(db, 'users', userId, 'iptv_sources', cleanSource.id);
      await setDoc(userDocRef, sanitizeForFirestore(cleanSource), { merge: true }).catch(() => {});
    }
  } catch (err) {
    console.warn('Error saving IPTV source to Firestore (saved locally):', err);
  }
}

/**
 * Delete an IPTV Source
 */
export async function deleteIptvSource(sourceId: string, userId?: string): Promise<void> {
  const current = getLocalIptvSources().filter(s => s.id !== sourceId);
  saveLocalIptvSources(current);

  try {
    const docRef = doc(db, 'iptv_sources', sourceId);
    await deleteDoc(docRef);

    if (userId) {
      const userDocRef = doc(db, 'users', userId, 'iptv_sources', sourceId);
      await deleteDoc(userDocRef).catch(() => {});
    }
  } catch (err) {
    console.warn('Error deleting IPTV source from Firestore:', err);
  }
}

/**
 * Subscribe to User IPTV Favorites
 */
export function subscribeToIptvFavorites(
  userId: string,
  onFavoritesChange: (favorites: IptvFavorite[]) => void
): () => void {
  if (!userId) {
    onFavoritesChange([]);
    return () => {};
  }

  const getLocalFavorites = (): IptvFavorite[] => {
    try {
      const raw = localStorage.getItem(`${IPTV_FAVORITES_LOCAL_KEY}_${userId}`);
      if (raw) return JSON.parse(raw);
    } catch {}
    return [];
  };

  onFavoritesChange(getLocalFavorites());

  try {
    const favsCol = collection(db, 'users', userId, 'iptv_favorites');
    return onSnapshot(
      favsCol,
      (snapshot) => {
        const favs = snapshot.docs.map(d => ({
          ...d.data(),
          id: d.id
        })) as IptvFavorite[];

        try {
          localStorage.setItem(`${IPTV_FAVORITES_LOCAL_KEY}_${userId}`, JSON.stringify(favs));
        } catch {}
        onFavoritesChange(favs);
      },
      () => {
        onFavoritesChange(getLocalFavorites());
      }
    );
  } catch {
    return () => {};
  }
}

/**
 * Toggle IPTV Favorite
 */
export async function toggleIptvFavorite(favorite: Omit<IptvFavorite, 'id'>, userId: string): Promise<boolean> {
  if (!userId) return false;

  const favId = `${userId}_${favorite.type}_${encodeURIComponent(favorite.itemId)}`;
  const favDocRef = doc(db, 'users', userId, 'iptv_favorites', favId);

  try {
    const snap = await getDoc(favDocRef);
    if (snap.exists()) {
      await deleteDoc(favDocRef);
      return false; // removed
    } else {
      const newFav: IptvFavorite = {
        ...favorite,
        id: favId,
        userId,
        addedAt: new Date().toISOString()
      };
      await setDoc(favDocRef, sanitizeForFirestore(newFav));
      return true; // added
    }
  } catch (err) {
    console.warn('Toggle IPTV favorite error:', err);
    return false;
  }
}


